import React, { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { api } from "@/api";
import { Building2, Image as ImageIcon, Upload, Trash2 } from "lucide-react";

const ENTITY_DEFS = [
  { code: "BDM", label: "Blanc des Montagnes", color: "violet" },
  { code: "BDM_PARTICULIER", label: "BDM Particulier", color: "amber" },
];

const FIELDS = [
  { key: "name", label: "Raison sociale", type: "text" },
  { key: "tagline", label: "Activité (sous-titre)", type: "text" },
  { key: "legal_form", label: "Forme juridique", type: "text" },
  { key: "siret", label: "SIRET (affiché sur la facture)", type: "text" },
  { key: "siren", label: "SIREN", type: "text" },
  { key: "tva_label", label: "Libellé TVA", type: "text" },
  { key: "vat_rate", label: "Taux TVA (ex: 0.10 pour 10%)", type: "number", step: "0.01" },
  { key: "address1", label: "Adresse (ligne 1)", type: "text" },
  { key: "address2", label: "Adresse (ligne 2)", type: "text" },
  { key: "phone", label: "Téléphone", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "iban", label: "IBAN", type: "text" },
  { key: "bic", label: "BIC", type: "text" },
  { key: "payment_delay_days", label: "Délai de paiement (jours)", type: "number" },
];

function EntityCard({ code, label, color, data, onSaved }) {
  const [form, setForm] = useState(data || {});
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setForm(data || {});
    let revokeUrl = null;
    if (data?.logo_gridfs_id) {
      // Récupère le logo via l'axios api client (Authorization auto-injecté)
      api.get(`/admin/settings/entities/${code}/logo`, { responseType: "blob" })
        .then((res) => {
          if (res?.data) {
            revokeUrl = URL.createObjectURL(res.data);
            setLogoUrl(revokeUrl);
          }
        })
        .catch(() => setLogoUrl(null));
    } else {
      setLogoUrl(null);
    }
    return () => {
      if (revokeUrl) URL.revokeObjectURL(revokeUrl);
    };
  }, [data, code]);

  const update = (k, v) => setForm((prev) => ({ ...prev, [k]: v }));

  const save = async () => {
    try {
      setSaving(true);
      const payload = { ...form };
      // Convert numeric fields
      if (payload.vat_rate !== undefined && payload.vat_rate !== "") payload.vat_rate = parseFloat(payload.vat_rate);
      if (payload.payment_delay_days !== undefined && payload.payment_delay_days !== "") payload.payment_delay_days = parseInt(payload.payment_delay_days, 10);
      const res = await api.put(`/admin/settings/entities/${code}`, payload);
      toast({ title: "Paramètres sauvegardés", description: `${label} a été mis à jour.` });
      onSaved?.(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: "Échec de la sauvegarde.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Trop volumineux", description: "Le logo doit faire moins de 5 Mo.", variant: "destructive" });
      return;
    }
    try {
      setUploadingLogo(true);
      const fd = new FormData();
      fd.append("logo", file);
      const res = await api.post(`/admin/settings/entities/${code}/logo`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast({ title: "Logo mis à jour", description: `Logo de ${label} enregistré.` });
      onSaved?.(res.data);
    } catch (err) {
      toast({ title: "Erreur", description: "Échec de l'envoi du logo.", variant: "destructive" });
    } finally {
      setUploadingLogo(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const removeLogo = async () => {
    try {
      const res = await api.delete(`/admin/settings/entities/${code}/logo`);
      setLogoUrl(null);
      toast({ title: "Logo supprimé" });
      onSaved?.(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: "Impossible de supprimer le logo.", variant: "destructive" });
    }
  };

  const colorBorder = color === "violet" ? "border-violet-500/40" : "border-amber-500/40";
  const colorBg = color === "violet" ? "bg-violet-500/5" : "bg-amber-500/5";
  const colorBtn = color === "violet" ? "bg-violet-600 hover:bg-violet-500" : "bg-amber-600 hover:bg-amber-500";

  return (
    <div data-testid={`entity-card-${code}`} className={`rounded-xl border ${colorBorder} ${colorBg} p-6`}>
      <div className="flex items-center gap-3 mb-4">
        <Building2 className={`h-5 w-5 ${color === "violet" ? "text-violet-400" : "text-amber-400"}`} />
        <h3 className="text-lg font-semibold text-white">{label}</h3>
        <span className="text-xs text-slate-500 font-mono">{code}</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-slate-300 text-xs">{f.label}</Label>
            <Input
              data-testid={`entity-${code}-${f.key}`}
              type={f.type}
              step={f.step}
              value={form[f.key] ?? ""}
              onChange={(e) => update(f.key, e.target.value)}
              className="bg-slate-950 border-slate-700 text-slate-200 mt-1"
            />
          </div>
        ))}
      </div>

      <div className="border-t border-slate-700/50 pt-4 mb-4">
        <Label className="text-slate-300 text-sm flex items-center gap-2">
          <ImageIcon className="h-4 w-4" /> Logo
        </Label>
        <div className="flex items-center gap-4 mt-2">
          {logoUrl ? (
            <img src={logoUrl} alt={`Logo ${label}`} className="h-16 w-auto bg-white rounded p-1" />
          ) : (
            <div className="h-16 w-16 bg-slate-800 rounded flex items-center justify-center text-slate-500 text-xs">aucun</div>
          )}
          <input
            data-testid={`entity-${code}-logo-input`}
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={uploadLogo}
          />
          <Button
            data-testid={`entity-${code}-logo-upload`}
            onClick={() => fileInputRef.current?.click()}
            disabled={uploadingLogo}
            variant="outline"
            className="border-slate-600"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploadingLogo ? "Envoi..." : logoUrl ? "Remplacer" : "Choisir un logo"}
          </Button>
          {logoUrl && (
            <Button
              data-testid={`entity-${code}-logo-remove`}
              onClick={removeLogo}
              variant="outline"
              className="border-red-700 text-red-400"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-xs text-slate-500 mt-2">PNG ou JPG, 5 Mo max. Stocké dans MongoDB GridFS — persiste entre les déploiements.</p>
      </div>

      <div className="flex justify-end">
        <Button data-testid={`entity-${code}-save`} onClick={save} disabled={saving} className={colorBtn}>
          {saving ? "Sauvegarde..." : `Sauvegarder ${label}`}
        </Button>
      </div>
    </div>
  );
}

export default function AdminEntitySettings() {
  const [entities, setEntities] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/admin/settings/entities");
      setEntities(res.data);
    } catch (e) {
      toast({ title: "Erreur", description: "Impossible de charger les entités.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  if (loading) return <div className="text-slate-400 py-8 text-center">Chargement...</div>;
  if (!entities) return null;

  return (
    <div className="space-y-6">
      <div className="rounded-xl border border-white/10 bg-white/[0.04] p-4">
        <p className="text-sm text-slate-300">
          Configurez les coordonnées, IBAN, TVA et logo de chaque entité émettrice. Ces paramètres sont stockés dans MongoDB
          et <strong>persistent entre tous les déploiements</strong>. Les factures utiliseront ces valeurs au moment de leur génération.
        </p>
      </div>
      {ENTITY_DEFS.map((def) => (
        <EntityCard
          key={def.code}
          code={def.code}
          label={def.label}
          color={def.color}
          data={entities[def.code]}
          onSaved={(updated) => setEntities((prev) => ({ ...prev, [def.code]: updated }))}
        />
      ))}
    </div>
  );
}
